import { unsafeSVG } from 'lit-html/directives/unsafe-svg.js';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import TooltipScss from './tooltip.scss?inline';

import infoIcon from '@kyndryl-design-system/shidoka-icons/svg/monochrome/20/information.svg';

/**
 * Tooltip.
 * @fires on-tooltip-toggle - Emits the open state of the tooltip on open/close. `detail:{ open: boolean }`
 * @slot unnamed - Slot for tooltip content.
 * @slot anchor - Slot for custom anchor button content.
 */
@customElement('kyn-tooltip')
export class Tooltip extends LitElement {
  static override styles = unsafeCSS(TooltipScss);

  /** Assistive text for anchor button. */
  @property({ type: String })
  accessor assistiveText = 'Tooltip';

  /** Render a passive span anchor instead of a button.
   * Useful when the tooltip trigger lives inside another interactive element.
   */
  @property({ type: Boolean, attribute: 'non-interactive-anchor' })
  accessor nonInteractiveAnchor = false;

  /** Renders the tooltip with tighter spacing. */
  @property({ type: Boolean })
  accessor compact = false;

  /** Tooltip open state.
   * @internal
   */
  @state()
  accessor _open = false;

  /** Tooltip anchor position. `'start'`, `'end'`, or `'center'`.
   * @internal
   */
  @state()
  accessor _anchorPosition = 'center';

  /** Tooltip direction. `'top'`, `'bottom'`, `'left'`, or `'right'`.
   * @internal
   */
  @state()
  accessor _direction = 'top';

  /** Timeout function to delay modal close.
   * @internal
   */
  @state()
  accessor _timer: any;

  /** Anchor element
   * @internal
   */
  @query('.anchor')
  accessor _anchorEl!: any;

  /** Content element
   * @internal
   */
  @query('.content')
  accessor _contentEl!: any;

  override render() {
    const classes = {
      content: true,
      compact: this.compact,
      open: this._open,
      'anchor--start': this._anchorPosition === 'start',
      'anchor--end': this._anchorPosition === 'end',
      'anchor--center': this._anchorPosition === 'center',
      'direction--top': this._direction === 'top',
      'direction--bottom': this._direction === 'bottom',
      'direction--left': this._direction === 'left',
      'direction--right': this._direction === 'right',
    };

    return html`
      <div class="tooltip">
        ${this.nonInteractiveAnchor
          ? html`
              <span
                class="anchor anchor--passive"
                @mouseenter=${this._handleOpen}
                @mouseleave=${this._handleMouseLeave}
              >
                <slot name="anchor"
                  ><span class="info-icon">${unsafeSVG(infoIcon)}</span></slot
                >
              </span>
            `
          : html`
              <button
                class="anchor"
                aria-label=${this.assistiveText}
                title=${this.assistiveText}
                aria-describedby="tooltip"
                @mouseenter=${this._handleOpen}
                @mouseleave=${this._handleMouseLeave}
                @focus=${this._handleOpen}
                @blur=${this._handleClose}
              >
                <slot name="anchor"
                  ><span class="info-icon">${unsafeSVG(infoIcon)}</span></slot
                >
              </button>
            `}

        <div
          id="tooltip"
          aria-hidden=${!this._open}
          role="tooltip"
          class=${classMap(classes)}
          @mouseenter=${this._handleOpen}
          @mouseleave=${this._handleMouseLeave}
        >
          <slot></slot>
        </div>
      </div>
    `;
  }

  private _positionTooltip() {
    const AnchorTop = this._anchorEl.getBoundingClientRect().top;
    const AnchorMiddle =
      this._anchorEl.getBoundingClientRect().top +
      this._anchorEl.getBoundingClientRect().height / 2;
    const AnchorBottom =
      this._anchorEl.getBoundingClientRect().top +
      this._anchorEl.getBoundingClientRect().height;
    const AnchorLeft = this._anchorEl.getBoundingClientRect().left;
    const AnchorCenter =
      this._anchorEl.getBoundingClientRect().left +
      this._anchorEl.getBoundingClientRect().width / 2;
    const AnchorRight =
      this._anchorEl.getBoundingClientRect().left +
      this._anchorEl.getBoundingClientRect().width;
    const ViewportHeight = window.innerHeight;
    const ViewportWidth = window.innerWidth;

    // Edge case handling: When tooltip collide with kyn-header, force the tooltip to render downwards.
    const header = document.querySelector('kyn-header');
    const headerRect = header?.getBoundingClientRect();
    const tooltipHeight = this._contentEl.offsetHeight || 60;

    const isOverlappingHeader =
      headerRect && AnchorTop - tooltipHeight < headerRect.bottom;

    if (isOverlappingHeader) {
      this._direction = 'bottom';
      const center = AnchorLeft + this._anchorEl.offsetWidth / 2;

      if (center < ViewportWidth * 0.33) {
        this._anchorPosition = 'start';
      } else if (center < ViewportWidth * 0.66) {
        this._anchorPosition = 'center';
      } else {
        this._anchorPosition = 'end';
      }

      this._contentEl.style.top = `${AnchorBottom}px`;
      this._contentEl.style.left = `${center}px`;
      return;
    }

    let vertical = 'down';
    let horizontal = 'right';

    if (AnchorTop > ViewportHeight * 0.67) {
      vertical = 'up';
    } else if (AnchorTop > ViewportHeight * 0.33) {
      vertical = 'middle';
    }

    if (AnchorLeft > ViewportWidth * 0.67) {
      horizontal = 'left';
    } else if (AnchorLeft > ViewportWidth * 0.33) {
      horizontal = 'center';
    }

    if (vertical === 'down') {
      if (horizontal === 'right') {
        if (ViewportWidth < 672) {
          this._direction = 'bottom';
          this._anchorPosition = 'start';

          this._contentEl.style.top = AnchorBottom + 'px';
          this._contentEl.style.left = AnchorCenter + 'px';
        } else {
          this._direction = 'right';
          this._anchorPosition = 'start';

          this._contentEl.style.top = AnchorMiddle + 'px';
          this._contentEl.style.left = AnchorRight + 'px';
        }
      } else if (horizontal === 'center') {
        this._direction = 'bottom';
        this._anchorPosition = 'center';

        this._contentEl.style.top = AnchorBottom + 'px';
        this._contentEl.style.left = AnchorCenter + 'px';
      } else {
        if (ViewportWidth < 672) {
          this._direction = 'bottom';
          this._anchorPosition = 'end';

          this._contentEl.style.top = AnchorBottom + 'px';
          this._contentEl.style.left = AnchorCenter + 'px';
        } else {
          this._direction = 'left';
          this._anchorPosition = 'start';

          this._contentEl.style.top = AnchorMiddle + 'px';
          this._contentEl.style.left = AnchorLeft + 'px';
        }
      }
    } else if (vertical === 'middle') {
      if (horizontal === 'right') {
        if (ViewportWidth < 672) {
          this._direction = 'top';
          this._anchorPosition = 'start';

          this._contentEl.style.top = AnchorTop + 'px';
          this._contentEl.style.left = AnchorCenter + 'px';
        } else {
          this._direction = 'right';
          this._anchorPosition = 'center';

          this._contentEl.style.top = AnchorMiddle + 'px';
          this._contentEl.style.left = AnchorRight + 'px';
        }
      } else if (horizontal === 'center') {
        this._direction = 'top';
        this._anchorPosition = 'center';

        this._contentEl.style.top = AnchorTop + 'px';
        this._contentEl.style.left = AnchorCenter + 'px';
      } else {
        if (ViewportWidth < 672) {
          this._direction = 'top';
          this._anchorPosition = 'end';

          this._contentEl.style.top = AnchorTop + 'px';
          this._contentEl.style.left = AnchorCenter + 'px';
        } else {
          this._direction = 'left';
          this._anchorPosition = 'center';

          this._contentEl.style.top = AnchorMiddle + 'px';
          this._contentEl.style.left = AnchorLeft + 'px';
        }
      }
    } else {
      if (horizontal === 'right') {
        if (ViewportWidth < 672) {
          this._direction = 'top';
          this._anchorPosition = 'start';

          this._contentEl.style.top = AnchorTop + 'px';
          this._contentEl.style.left = AnchorCenter + 'px';
        } else {
          this._direction = 'right';
          this._anchorPosition = 'end';

          this._contentEl.style.top = AnchorMiddle + 'px';
          this._contentEl.style.left = AnchorRight + 'px';
        }
      } else if (horizontal === 'center') {
        this._direction = 'top';
        this._anchorPosition = 'center';

        this._contentEl.style.top = AnchorTop + 'px';
        this._contentEl.style.left = AnchorCenter + 'px';
      } else {
        if (ViewportWidth < 672) {
          this._direction = 'top';
          this._anchorPosition = 'end';

          this._contentEl.style.top = AnchorTop + 'px';
          this._contentEl.style.left = AnchorCenter + 'px';
        } else {
          this._direction = 'left';
          this._anchorPosition = 'end';

          this._contentEl.style.top = AnchorMiddle + 'px';
          this._contentEl.style.left = AnchorLeft + 'px';
        }
      }
    }
  }

  private _handleOpen() {
    clearTimeout(this._timer);
    this._positionTooltip();

    setTimeout(() => {
      this._open = true;
    }, 100);
  }

  private _handleClose() {
    this._open = false;
  }

  private _handleMouseLeave() {
    this._timer = setTimeout(() => {
      this._open = false;
      clearTimeout(this._timer);
    }, 500);
  }

  private _handleEsc(e: KeyboardEvent) {
    if (this._open && e.key === 'Escape') {
      this._open = false;
    }
  }

  private _emitToggle() {
    const event = new CustomEvent('on-tooltip-toggle', {
      detail: { open: this._open },
    });
    this.dispatchEvent(event);
  }

  override updated(changedProps: any) {
    if (changedProps.has('_open') && changedProps.get('_open') !== undefined) {
      this._emitToggle();
    }
  }

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', (e) => this._handleEsc(e));
  }

  override disconnectedCallback() {
    document.removeEventListener('keydown', (e) => this._handleEsc(e));
    super.disconnectedCallback();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kyn-tooltip': Tooltip;
  }
}
